import { useState, useEffect, useCallback } from "react";
import api from "../api.js";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Titulo = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1a5276;
  margin: 0 0 4px 0;
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  background-color: #1a5276;
  color: white;
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;

  &:last-child {
    text-align: right;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: middle;

  &:last-child {
    text-align: right;
  }
`;

const Tr = styled.tr`
  background-color: ${({ $par }) => ($par ? "#f8f9fa" : "white")};
  &:hover {
    background-color: #eaf4fb;
  }
`;

const TrTotal = styled.tr`
  background-color: #1a5276;
  color: white;
`;

const TdTotal = styled.td`
  padding: 12px 14px;
  font-weight: 700;
  font-size: 15px;

  &:last-child {
    text-align: right;
  }
`;

const SaldoValor = styled.span`
  font-weight: 700;
  color: ${({ $negativo }) => ($negativo ? "#c0392b" : "#27ae60")};
`;

const Topo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const BtnAtualizar = styled.button`
  padding: 7px 16px;
  border: none;
  background-color: #2980b9;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;

  &:hover { background-color: #1a6a9a; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Vazio = styled.div`
  text-align: center;
  color: #888;
  padding: 32px;
  font-style: italic;
`;

const InfoBox = styled.div`
  background: #eaf4fb;
  border: 1px solid #aed6f1;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  color: #2c3e50;
`;

function fmtValor(v) {
  return "R$ " + Number(v || 0).toFixed(2).replace(".", ",");
}

export default function AdminSaldos() {
  const [usuarios, setUsuarios] = useState([]);
  const [totalGeral, setTotalGeral] = useState(0);
  const [carregando, setCarregando] = useState(false);

  const carregarSaldos = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await api.get("liga/admin/saldos");
      setUsuarios(res.data.usuarios || []);
      setTotalGeral(res.data.totalGeral || 0);
    } catch (e) {
      console.error("Erro ao carregar saldos:", e);
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    carregarSaldos();
  }, [carregarSaldos]);

  const comSaldo = usuarios.filter(u => u.saldo > 0).length;
  const comDebito = usuarios.filter(u => u.saldo < 0).length;

  return (
    <Container>
      <Topo>
        <Titulo>Saldos dos Usuários</Titulo>
        <BtnAtualizar onClick={carregarSaldos} disabled={carregando}>
          {carregando ? "Carregando..." : "Atualizar"}
        </BtnAtualizar>
      </Topo>

      <InfoBox>
        Use para confrontar o saldo total da plataforma com o saldo no Mercado Pago (ou outra entidade financeira).
        {comDebito > 0 && (
          <span style={{ color: "#c0392b", marginLeft: 8 }}>
            ⚠ {comDebito} usuário(s) com saldo negativo.
          </span>
        )}
      </InfoBox>

      {carregando ? (
        <Vazio>Carregando...</Vazio>
      ) : usuarios.length === 0 ? (
        <Vazio>Nenhum usuário encontrado.</Vazio>
      ) : (
        <Tabela>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Usuário</Th>
              <Th style={{ textAlign: "right" }}>Saldo</Th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u, idx) => (
              <Tr key={u.id} $par={idx % 2 === 0}>
                <Td style={{ color: "#888", fontSize: "12px" }}>{idx + 1}</Td>
                <Td>{u.nome}</Td>
                <Td>
                  <SaldoValor $negativo={u.saldo < 0}>
                    {fmtValor(u.saldo)}
                  </SaldoValor>
                </Td>
              </Tr>
            ))}
          </tbody>
          <tfoot>
            <TrTotal>
              <TdTotal colSpan={2}>
                Total geral ({usuarios.length} usuário{usuarios.length !== 1 ? "s" : ""},
                {" "}{comSaldo} com saldo positivo)
              </TdTotal>
              <TdTotal>
                {fmtValor(totalGeral)}
              </TdTotal>
            </TrTotal>
          </tfoot>
        </Tabela>
      )}
    </Container>
  );
}
